import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Import slide images
import slide1 from "@/../public/images/lambrines/imagesSlides/Recepcion.png";
import slide2 from "@/../public/images/lambrines/imagesSlides/Sala1.png";
import slide3 from "@/../public/images/lambrines/imagesSlides/oficina1.png";

const slides = [
  { id: 1, image: slide1, alt: "Recepción elegante con lambrines" },
  { id: 2, image: slide2, alt: "Sala moderna con acabados premium" },
  { id: 3, image: slide3, alt: "Oficina profesional con diseño contemporáneo" },
];

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Auto-advance slides every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 4000);

    return () => clearInterval(timer);
  }, [currentSlide]);

  const nextSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setTimeout(() => setIsTransitioning(false), 1000);
    }
  };

  const prevSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      setTimeout(() => setIsTransitioning(false), 1000);
    }
  };

  const goToSlide = (index: number) => {
    if (!isTransitioning && index !== currentSlide) {
      setIsTransitioning(true);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 1000);
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative w-full h-screen overflow-hidden">
      {/* Background Images with Fade Effect */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Content - Centered Text with Animations */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Marca principal */}
          <div className="mb-4 hero-fade-in-1">
            <Link 
              to="/" 
              className="block font-playfair font-extrabold text-[#C6A676] drop-shadow-lg text-6xl md:text-[5.3rem] leading-tight tracking-tight uppercase hero-breathe-anim hover:opacity-90 transition-opacity"
            >
              El Samán
            </Link>
          </div>

          {/* Título principal */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-semibold text-[#e7ded1] text-center mb-4 hero-fade-in-2 drop-shadow-sm">
            Lambrines <span className="bg-gradient-to-r from-[#B7A179] via-[#e6d7a5] to-[#C6A76D] bg-clip-text text-transparent">premium</span>
          </h1>
          {/* Línea dorada decorativa */}
          <div className="mx-auto mb-6 w-24 h-1 bg-gradient-to-r from-transparent via-[#C6A76D] to-transparent hero-fade-in-3 rounded-full" />

          {/* Subtítulo */}
          <div className="relative hero-fade-in-4">
            <p className="text-[#d2bb98] text-base md:text-lg text-center max-w-2xl mx-auto font-normal tracking-wide drop-shadow">
              Diseños modernos <span className="mx-2 text-[#C6A76D]">•</span> Calidad garantizada <span className="mx-2 text-[#C6A76D]">•</span> Envío nacional
            </p>
          </div>

          {/* Animated Button */}
          <button
            onClick={scrollToContact}
            className="mt-8 bg-[#C6A76D] text-black px-10 py-4 rounded-full font-bold text-lg hover:bg-[#e2c98a] transition-all duration-300 hover:scale-110 hover:shadow-2xl shadow-lg hero-fade-in-5 relative overflow-hidden group"
          >
            <span className="relative z-10">Solicitar cotización</span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#e2c98a] via-white to-[#e2c98a] opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
          </button>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
        aria-label="Slide anterior"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
        aria-label="Siguiente slide"
      >
        <ChevronRight size={24} />
      </button>

      {/* Navigation Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentSlide
                ? "w-12 h-3 bg-[#C6A76D]"
                : "w-3 h-3 bg-white/50 hover:bg-white/80"
            }`}
            aria-label={`Ir al slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
